import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utility function to merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isValidUrl(
  value: string,
  opts: { httpsOnly?: boolean } = {},
): boolean {
  if (!value) return false;
  try {
    const u = new URL(value);
    if (opts.httpsOnly) return u.protocol === "https:";
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Format date for display
 */
export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...options,
  }).format(dateObj);
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  const intervals = [
    { label: "year", seconds: 31536000 },
    { label: "month", seconds: 2592000 },
    { label: "day", seconds: 86400 },
    { label: "hour", seconds: 3600 },
    { label: "minute", seconds: 60 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(diffInSeconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count > 1 ? "s" : ""} ago`;
    }
  }

  return "Just now";
}

/**
 * Sleep utility for debugging/testing
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Generate a slug from a string
 */
export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with dashes
    .replace(/-+/g, "-") // Replace multiple dashes with single dash
    .trim();
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + "...";
}

/**
 * Timezone conversion utilities
 */

/**
 * Convert UTC time string (HH:MM) to target timezone
 * @param utcTime - Time in UTC format "HH:MM"
 * @param targetTimezone - Target timezone (e.g., "Asia/Calcutta", "America/New_York")
 * @returns Time string in target timezone "HH:MM"
 */
export function convertUTCTimeToTimezone(
  utcTime: string,
  targetTimezone: string
): string {
  if (!utcTime || !utcTime.includes(":")) {
    return utcTime;
  }

  const [hoursStr, minutesStr] = utcTime.split(":");
  const hours = parseInt(hoursStr || "0", 10);
  const minutes = parseInt(minutesStr || "0", 10);

  if (isNaN(hours) || isNaN(minutes)) {
    return utcTime;
  }

  // Create a date object for today with the UTC time
  const utcDate = new Date();
  utcDate.setUTCHours(hours, minutes, 0, 0);

  // Format the time in the target timezone
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: targetTimezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return formatter.format(utcDate);
}

/**
 * Convert timezone time string (HH:MM) back to UTC
 * @param localTime - Time in local timezone format "HH:MM"
 * @param sourceTimezone - Source timezone (e.g., "Asia/Calcutta", "America/New_York")
 * @returns Time string in UTC "HH:MM"
 */
export function convertTimezoneTimeToUTC(
  localTime: string,
  sourceTimezone: string
): string {
  if (!localTime || !localTime.includes(":")) {
    return localTime;
  }

  const [hoursStr, minutesStr] = localTime.split(":");
  const hours = parseInt(hoursStr || "0", 10);
  const minutes = parseInt(minutesStr || "0", 10);

  if (isNaN(hours) || isNaN(minutes)) {
    return localTime;
  }

  // Create a date object for today
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDate();

  // Create a date in the source timezone
  const localDate = new Date();
  localDate.setFullYear(year, month, day);
  localDate.setHours(hours, minutes, 0, 0);

  // Get the timezone offset for the source timezone
  const tempDate = new Date(
    localDate.toLocaleString("en-US", { timeZone: sourceTimezone })
  );
  const utcDate = new Date(
    localDate.toLocaleString("en-US", { timeZone: "UTC" })
  );
  const offset = utcDate.getTime() - tempDate.getTime();

  // Apply the offset to get UTC time
  const resultDate = new Date(localDate.getTime() + offset);

  // Format as HH:MM
  const utcHours = resultDate.getUTCHours().toString().padStart(2, "0");
  const utcMinutes = resultDate.getUTCMinutes().toString().padStart(2, "0");

  return `${utcHours}:${utcMinutes}`;
}

/**
 * Convert an array of time slots from UTC to target timezone
 * @param timeSlots - Array of time slot objects with startTime and endTime
 * @param targetTimezone - Target timezone
 * @returns Array of converted time slots
 */
export function convertTimeSlotsToTimezone<
  T extends { startTime: string; endTime: string },
>(timeSlots: T[], targetTimezone: string): T[] {
  return timeSlots.map((slot) => ({
    ...slot,
    startTime: convertUTCTimeToTimezone(slot.startTime, targetTimezone),
    endTime: convertUTCTimeToTimezone(slot.endTime, targetTimezone),
  }));
}

/**
 * Convert minutes since start of day from UTC to target timezone
 * @param utcMinutes - Minutes since start of day in UTC
 * @param targetTimezone - Target timezone
 * @returns Minutes since start of day in target timezone
 */
export function convertUTCMinutesToTimezone(
  utcMinutes: number,
  targetTimezone: string
): number {
  const hours = Math.floor(utcMinutes / 60);
  const minutes = utcMinutes % 60;

  const utcTime = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  const localTime = convertUTCTimeToTimezone(utcTime, targetTimezone);

  const [localHoursStr, localMinutesStr] = localTime.split(":");
  const localHours = parseInt(localHoursStr || "0", 10);
  const localMinutesValue = parseInt(localMinutesStr || "0", 10);

  if (isNaN(localHours) || isNaN(localMinutesValue)) {
    return utcMinutes; // fallback to original value
  }

  return localHours * 60 + localMinutesValue;
}

/**
 * Get timezone offset in hours from UTC
 * @param timezone - Timezone identifier
 * @returns Offset in hours (positive for ahead of UTC, negative for behind)
 */
export function getTimezoneOffset(timezone: string): number {
  const now = new Date();
  const utc = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
  const local = new Date(now.toLocaleString("en-US", { timeZone: timezone }));

  return (local.getTime() - utc.getTime()) / (1000 * 60 * 60);
}
